// collapse behaviour — state side + one-shot animation adapter.
//
// cleanSlate has no underlying framework collapse delegate (unlike bootstrap5's
// Bootstrap), so the trigger CLICK wiring — which the contract says a delegate-less
// tier MUST provide (base behaviors.contract.md / behaviors.js collapse note) —
// lives in scripts/collapse.js as a document-level delegate. This hook owns the
// state classes (stateClass / createInstances) and the add-row slide-in (create).
// Starts from base's collapse so any unimplemented bits keep their throwing stub.

import baseBehaviors from '../../../../../core/themes/base/scripts/behaviors.js';

const collapse = Object.assign({}, baseBehaviors.collapse, {
    // Resolve a runtime collapse-state name to cleanSlate's literal class.
    //   root   -> the marker that makes a region clip + animate its height
    //   shown  -> expanded,  hidden -> collapsed
    // Single tokens (no whitespace); unknown names throw to surface typos.
    stateClass: function (name) {
        const tokens = {
            root: 'zit-cs-collapsible',
            shown: 'zit-cs-collapse-shown',
            hidden: 'zit-cs-collapse-hidden'
        };
        const token = tokens[name];

        if (typeof token === typeof undefined) {
            throw new Error('zit collapse.stateClass: unknown state "' + name + '"');
        }

        return token;
    },

    // Apply initial state classes to every [data-zit-collapsible] region inside
    // rootEl (inclusive). Mirrors bootstrap5's createInstances: root + shown or
    // hidden (from [data-zit-initially-collapsed], default expanded), idempotent
    // via the data-zit-collapse-initialized marker. Trigger clicks are NOT wired
    // here (see scripts/collapse.js). Defaults to document for the boot-time scan.
    createInstances: function (rootEl) {
        if (typeof rootEl === typeof undefined) {
            rootEl = document;
        }

        // Resolve tokens via this.stateClass so a future override is honoured.
        const rootClass = this.stateClass('root');
        const shownClass = this.stateClass('shown');
        const hiddenClass = this.stateClass('hidden');

        // rootEl itself (when a region) plus its descendant regions.
        const regions = [];
        if (rootEl.nodeType === 1 && rootEl.matches('[data-zit-collapsible]')) {
            regions.push(rootEl);
        }
        rootEl.querySelectorAll('[data-zit-collapsible]').forEach((el) => regions.push(el));

        regions.forEach((el) => {
            // skip regions already initialised (boot, then per-subtree re-scans)
            if (el.dataset.zitCollapseInitialized === 'true') {
                return;
            }
            // root marker (clip + transition) ...
            el.classList.add(...rootClass.split(' '));
            // ... plus the initial visibility (default expanded when attr absent)
            const initiallyCollapsed = el.dataset.zitInitiallyCollapsed === 'true';
            const initialStateClass = initiallyCollapsed ? hiddenClass : shownClass;
            el.classList.add(...initialStateClass.split(' '));
            el.dataset.zitCollapseInitialized = 'true';
        });
    },

    // Wrap an element as a collapsible region for one-shot show/hide animation.
    // editList.js uses this for the add-row slide-in: it stamps a new item,
    // calls create(itemEl, { toggle: false }), subscribes 'shown' (to strip the
    // collapse classes + dispose once expanded), then show(). We drive it with
    // the same state classes as the rest of the collapse system: root (clip +
    // max-height transition) starting from hidden, then swapped to shown so the
    // element animates open; 'shown'/'hidden' fire on the max-height transitionend
    // (with a timeout fallback). This is distinct from the per-item chevron
    // collapse, which is the document-level click delegate in scripts/collapse.js.
    create: function (el, opts) {
        const options = opts || {};
        const rootClass = this.stateClass('root');
        const shownClass = this.stateClass('shown');
        const hiddenClass = this.stateClass('hidden');
        const handlers = { shown: [], hidden: [] };

        // Start collapsed so show() has something to animate from.
        el.classList.add(...rootClass.split(' '));
        el.classList.remove(...shownClass.split(' '));
        el.classList.add(...hiddenClass.split(' '));

        let pendingEnd = null;

        // Fire a lifecycle event once the max-height transition finishes; a
        // timeout fallback covers reduced-motion / missed transitionend.
        const fireAfterTransition = function (name) {
            const onEnd = function (e) {
                if (e && (e.target !== el || e.propertyName !== 'max-height')) {
                    return;
                }
                el.removeEventListener('transitionend', onEnd);
                if (pendingEnd === onEnd) {
                    pendingEnd = null;
                }
                handlers[name].forEach((handler) => handler(el));
            };
            pendingEnd = onEnd;
            el.addEventListener('transitionend', onEnd);
            setTimeout(() => {
                if (pendingEnd === onEnd) {
                    onEnd(null);
                }
            }, 500);
        };

        const instance = {
            on: function (name, handler) {
                if (typeof handlers[name] === typeof undefined) {
                    throw new Error('zit collapse.create.on: unknown event "' + name + '"');
                }
                handlers[name].push(handler);
            },
            show: function () {
                // Next frame so the collapsed start state is committed first.
                requestAnimationFrame(() => {
                    el.classList.remove(...hiddenClass.split(' '));
                    el.classList.add(...shownClass.split(' '));
                    fireAfterTransition('shown');
                });
            },
            hide: function () {
                requestAnimationFrame(() => {
                    el.classList.remove(...shownClass.split(' '));
                    el.classList.add(...hiddenClass.split(' '));
                    fireAfterTransition('hidden');
                });
            },
            dispose: function () {
                if (pendingEnd !== null) {
                    el.removeEventListener('transitionend', pendingEnd);
                    pendingEnd = null;
                }
            }
        };

        // Contract default is toggle:true; editList always passes false.
        if (options.toggle === true) {
            instance.show();
        }

        return instance;
    }
});

export default collapse;
